import { useState, useEffect } from "react";
import { Routes, Route, useNavigate, useLocation, Navigate } from "react-router-dom";
import { 
  Button, 
  SidebarLayout,
  SidebarRail,
  RailItem,
  RailSpacer,
  Sidebar,
  SidebarHeader,
  SidebarItem,
  MainContent,
  Home,
  LayoutGrid,
  Users,
  Globe,
  LogOut,
  Shield,
  Settings
} from "@repo/o37-ui";
import { Briefcase, ScrollText, BarChart3, MessageSquare, FolderKanban, Palette, Building2, Building, CreditCard, FileSpreadsheet, Calculator, TrendingUp, ArrowLeftRight } from "lucide-react";
import HomeComp from "./pages/Home";
import UI from "./pages/UI";
import VDR from "./pages/VDR";
import UsersComp from "./pages/Users";
import DomainGroups from "./pages/DomainGroups";
import VDRSettings from "./pages/VDRSettings";
import ProfileSettings from "./pages/ProfileSettings";
import AdminLogs from "./pages/AdminLogs";
// Fundraising pages
import RaiseList from "./pages/Fundraising/RaiseList";
import RaiseDetail from "./pages/Fundraising/RaiseDetail";
import AllRaisesAnalytics from "./pages/Fundraising/AllRaisesAnalytics";
import FundDetail from "./pages/Fundraising/FundDetail";
import RaiseSettings from "./pages/Fundraising/RaiseSettings";
import TermSheetSettings from "./pages/Fundraising/TermSheetSettings";
// Holdings pages
import { CompanyList, CompanyDetail, PeopleList, CostsDashboard, SubscriptionDetail, HoldingsOverview, CostsByView, BankImportDashboard, ImportUpload, TransactionReview, TransactionList, VendorMappings, PlanList, PlanDetail, PlanComparison, RevenueList, RevenueImport, CompanyMargins, MercurySetup, RevenueForecastList, ExpectedVsActualDashboard } from "./pages/Holdings";
// Components
import AIAssistantSidebar from "./components/AIAssistantSidebar";
import { PermissionProvider, usePermissions } from "./PermissionContext";
import { useAdminTracker } from "./hooks/useAdminTracker";

type AdminPanelProps = {
  onLogout: () => void;
};

function AdminPanelContent({ onLogout }: AdminPanelProps) {
  const { hasPage, hasCategory } = usePermissions();
  const navigate = useNavigate();
  const location = useLocation();
  const userEmail = localStorage.getItem("app_email");
  
  // AI Sidebar state
  const [aiSidebarOpen, setAiSidebarOpen] = useState(false);
  const [currentFundraiseId, setCurrentFundraiseId] = useState<string | undefined>();
  
  // Initialize admin activity tracker
  useAdminTracker();

  // Determine active section and tab based on current pathname
  const getActiveState = () => {
    const path = location.pathname;
    
    // Define sections
    if (path.startsWith('/fundraising')) {
      let tab = 'raises';
      let isRaiseDetail = false;
      
      if (path.match(/\/raises\/[^/]+$/) && !path.includes('/new')) {
        // This is the RaiseDetail page (e.g., /fundraising/raises/123)
        tab = 'raises';
        isRaiseDetail = true;
      } else if (path.includes('/funds/')) {
        // Fund detail page - also needs full width
        tab = 'raises';
        isRaiseDetail = true;
      } else if (path.includes('/raises') && !path.includes('/settings')) {
        tab = 'raises';
      } else if (path.includes('/analytics')) {
        tab = 'analytics';
      } else if (path.includes('/term-sheet-settings')) {
        tab = 'term-sheet-settings';
      } else if (path.includes('/vdr')) {
        tab = 'vdr';
      } else if (path.includes('/domain-groups')) {
        tab = 'domain-groups';
      }
      
      return {
        section: 'fundraising',
        tab,
        isRaiseDetail
      };
    }
    
    if (path.startsWith('/settings')) {
        return {
            section: 'settings',
            tab: 'profile-settings',
            isRaiseDetail: false
        };
    }

    // Holdings section
    if (path.startsWith('/holdings')) {
      let tab = 'overview';
      if (path.includes('/revenue')) tab = 'revenue';
      else if (path.includes('/costs')) tab = 'costs';
      else if (path.includes('/transactions')) tab = 'transactions';
      else if (path.includes('/bank-imports')) tab = 'bank-imports';
      else if (path.includes('/planning')) tab = 'planning';
      else if (path.includes('/portfolio')) tab = 'portfolio';
      else if (path.includes('/people')) tab = 'people';

      return {
        section: 'holdings',
        tab,
        isRaiseDetail: false
      };
    }

    // Default to admin section
    let tab = 'home';
    if (path.startsWith('/admin/users')) tab = 'users';
    if (path.startsWith('/admin/logs')) tab = 'logs';
    if (path.startsWith('/admin/ui')) tab = 'ui';
    
    return {
      section: 'admin',
      tab,
      isRaiseDetail: false
    };
  };
  
  const { section, tab, isRaiseDetail } = getActiveState();

  // Extract fundraise ID from path - must be in useEffect to avoid setState during render
  useEffect(() => {
    const path = location.pathname;
    const commandCenterMatch = path.match(/\/command-center\/([^/]+)/);
    const raiseMatch = path.match(/\/raises\/([^/]+)/);
    if (commandCenterMatch && commandCenterMatch[1] !== 'new') {
      setCurrentFundraiseId(commandCenterMatch[1]);
    } else if (raiseMatch && raiseMatch[1] !== 'new') {
      setCurrentFundraiseId(raiseMatch[1]);
    }
  }, [location.pathname]);

  // Permission Checks
  const showHome = hasPage('admin.home');
  const showUsers = hasPage('admin.users');
  const showLogs = hasPage('admin.logs');
  
  const showVDR = hasPage('fundraising.vdr');
  const showDomainGroups = hasPage('fundraising.domain_groups');
  const showRaises = hasPage('fundraising.command_center') || hasCategory('fundraising');
  const showAnalytics = hasPage('fundraising.analytics') || hasCategory('fundraising');

  // Holdings permission checks
  const showHoldingsPortfolio = hasPage('holdings.portfolio') || hasCategory('holdings');
  const showHoldingsCosts = hasPage('holdings.costs') || hasCategory('holdings');
  const showHoldingsTransactions = hasPage('holdings.transactions') || hasCategory('holdings');
  const showHoldingsBankImports = hasPage('holdings.bank_imports') || hasCategory('holdings');
  const showHoldingsPlanning = hasPage('holdings.planning') || hasCategory('holdings');
  const showHoldingsRevenue = hasPage('holdings.revenue') || hasCategory('holdings');

  // Navigation handlers
  const navigateToAdmin = () => navigate('/admin/home');
  const navigateToFundraising = () => navigate('/fundraising/raises');
  const navigateToHoldings = () => navigate('/holdings/overview');
  const navigateToProfile = () => navigate('/settings/profile-settings');
  
  // Listen for AI chat events from RaiseDetail
  useEffect(() => {
    const handleOpenAIChat = (event: CustomEvent) => {
      setCurrentFundraiseId(event.detail?.raiseId);
      setAiSidebarOpen(true);
    };
    
    window.addEventListener('openAIChat', handleOpenAIChat as EventListener);
    return () => {
      window.removeEventListener('openAIChat', handleOpenAIChat as EventListener);
    };
  }, []);

  // Determine default redirect route based on permissions
  let defaultRoute = '/admin/no-access';
  
  if (section === 'fundraising') {
    if (showRaises) defaultRoute = '/fundraising/raises';
    else if (showAnalytics) defaultRoute = '/fundraising/analytics';
    else if (showVDR) defaultRoute = '/fundraising/vdr';
    else if (showDomainGroups) defaultRoute = '/fundraising/domain-groups';
    else if (showHome) defaultRoute = '/admin/home';
  } else {
    // Admin section defaults
    if (showHome) defaultRoute = '/admin/home';
    else if (showUsers) defaultRoute = '/admin/users';
    else if (showRaises) defaultRoute = '/fundraising/raises';
    else if (showVDR) defaultRoute = '/fundraising/vdr';
    else if (showDomainGroups) defaultRoute = '/fundraising/domain-groups';
  }

  // Check if sections should be visible in Rail
  const showAdminSection = hasCategory('admin');
  const showFundraisingSection = hasCategory('fundraising');
  const showHoldingsSection = hasCategory('holdings');

  return (
    <SidebarLayout>
      {/* Primary Rail - "Component where you switch between overall tabs" */}
      <SidebarRail>
        <div className="mb-4 flex items-center justify-center p-2">
           <img src="/o37-logo.svg" alt="o37" className="h-5 w-auto" />
        </div>

        {showAdminSection && (
            <RailItem 
            icon={Shield}
            label="Admin"
            active={section === 'admin'}
            onClick={navigateToAdmin}
            />
        )}

        {showFundraisingSection && (
            <RailItem
            icon={Briefcase}
            label="Fundraising"
            active={section === 'fundraising'}
            onClick={navigateToFundraising}
            />
        )}

        {showHoldingsSection && (
            <RailItem
            icon={Building2}
            label="Holdings"
            active={section === 'holdings'}
            onClick={navigateToHoldings}
            />
        )}

        <RailSpacer />

        {/* AI Assistant Toggle */}
        {showFundraisingSection && section === 'fundraising' && (
          <RailItem 
            icon={MessageSquare}
            label="AI Assistant"
            active={aiSidebarOpen}
            onClick={() => setAiSidebarOpen(!aiSidebarOpen)}
          />
        )}

        <div className="mb-2 flex flex-col items-center gap-2 pb-2">
            {/* User Profile / Logout area in Rail */}
            <RailItem 
                icon={LogOut}
                label="Logout"
                onClick={onLogout}
                className="text-neutral-400 hover:text-red-600"
            />
             {userEmail && (
                <button 
                    onClick={navigateToProfile}
                    className={`h-8 w-8 rounded-full flex items-center justify-center text-xs font-medium transition-all ${
                        section === 'settings' 
                            ? "bg-black text-white shadow-md ring-2 ring-neutral-200" 
                            : "bg-neutral-200 text-neutral-600 hover:bg-neutral-300"
                    }`}
                    title={userEmail}
                >
                    {userEmail.charAt(0).toUpperCase()}
                </button>
            )}
        </div>
      </SidebarRail>

      {/* Unified Container for Navigation and Content */}
      <div className={`flex-1 flex overflow-hidden my-3 mr-3 bg-white rounded-2xl border border-neutral-200 shadow-sm transition-all ${
        aiSidebarOpen ? 'mr-0' : ''
      }`}>
        {/* Secondary Sidebar - Navigation */}
        <Sidebar className="bg-white border-r border-neutral-100">
            <SidebarHeader>
            <h2 className="text-sm font-semibold text-neutral-900 px-2">
                {section === 'admin' ? 'Admin' : section === 'fundraising' ? 'Fundraising' : section === 'holdings' ? 'Holdings' : 'Settings'}
            </h2>
            </SidebarHeader>

            <div className="flex flex-col gap-1 px-3 mt-2">
            {section === 'admin' && (
                <>
                {showHome && (
                    <SidebarItem 
                    icon={Home}
                    label="Home"
                    active={tab === 'home'}
                    onClick={() => navigate('/admin/home')}
                    />
                )}

                {showUsers && (
                    <SidebarItem 
                    icon={Users}
                    label="Users"
                    active={tab === 'users'}
                    onClick={() => navigate('/admin/users')}
                    />
                )}

                {showLogs && (
                    <SidebarItem 
                    icon={ScrollText}
                    label="Logs"
                    active={tab === 'logs'}
                    onClick={() => navigate('/admin/logs')}
                    />
                )}

                <SidebarItem 
                    icon={Palette}
                    label="UI"
                    active={tab === 'ui'}
                    onClick={() => navigate('/admin/ui')}
                />
                </>
            )}

            {section === 'fundraising' && (
                <>
                {showRaises && (
                    <SidebarItem
                    icon={FolderKanban}
                    label="Your Raises"
                    active={tab === 'raises'}
                    onClick={() => navigate('/fundraising/raises')}
                    />
                )}

                {showAnalytics && (
                    <SidebarItem
                    icon={BarChart3}
                    label="Analytics"
                    active={tab === 'analytics'}
                    onClick={() => navigate('/fundraising/analytics')}
                    />
                )}

                {showVDR && (
                    <SidebarItem
                    icon={LayoutGrid}
                    label="VDR"
                    active={tab === 'vdr'}
                    onClick={() => navigate('/fundraising/vdr')}
                    />
                )}

                {showDomainGroups && (
                    <SidebarItem
                    icon={Globe}
                    label="Domain Groups"
                    active={tab === 'domain-groups'}
                    onClick={() => navigate('/fundraising/domain-groups')}
                    />
                )}
                </>
            )}

            {section === 'holdings' && (
                <>
                <SidebarItem
                    icon={Home}
                    label="Overview"
                    active={tab === 'overview'}
                    onClick={() => navigate('/holdings/overview')}
                />

                {showHoldingsRevenue && (
                    <SidebarItem
                    icon={TrendingUp}
                    label="Revenue"
                    active={tab === 'revenue'}
                    onClick={() => navigate('/holdings/revenue')}
                    />
                )}

                {showHoldingsCosts && (
                    <SidebarItem
                    icon={CreditCard}
                    label="Costs"
                    active={tab === 'costs'}
                    onClick={() => navigate('/holdings/costs')}
                    />
                )}

                {showHoldingsTransactions && (
                    <SidebarItem
                    icon={ArrowLeftRight}
                    label="Transactions"
                    active={tab === 'transactions'}
                    onClick={() => navigate('/holdings/transactions')}
                    />
                )}

                {showHoldingsBankImports && (
                    <SidebarItem
                    icon={FileSpreadsheet}
                    label="Bank Imports"
                    active={tab === 'bank-imports'}
                    onClick={() => navigate('/holdings/bank-imports')}
                    />
                )}

                {showHoldingsPlanning && (
                    <SidebarItem
                    icon={Calculator}
                    label="Planning"
                    active={tab === 'planning'}
                    onClick={() => navigate('/holdings/planning')}
                    />
                )}

                {showHoldingsPortfolio && (
                    <>
                    <SidebarItem
                    icon={Building}
                    label="Portfolio"
                    active={tab === 'portfolio'}
                    onClick={() => navigate('/holdings/portfolio')}
                    />
                    <SidebarItem
                    icon={Users}
                    label="People"
                    active={tab === 'people'}
                    onClick={() => navigate('/holdings/people')}
                    />
                    </>
                )}
                </>
            )}

            {section === 'settings' && (
                <SidebarItem 
                    icon={Settings}
                    label="Profile Settings"
                    active={tab === 'profile-settings'}
                    onClick={() => navigate('/settings/profile-settings')}
                />
            )}
            </div>
        </Sidebar>

        {/* Content Section */}
        <MainContent fullWidth={isRaiseDetail} noPadding={isRaiseDetail}>
            <Routes>
                {/* Redirect /admin to the first available route or no-access */}
                <Route index element={<Navigate to={defaultRoute} replace />} />
                
                {/* Admin Routes */}
                {(section === 'admin' || section === 'settings') && (
                    <>
                        <Route path="home" element={
                            showHome ? <HomeComp /> : <Navigate to={defaultRoute} replace />
                        } />
                        
                        <Route path="users" element={
                            showUsers ? <UsersComp /> : <Navigate to={defaultRoute} replace />
                        } />

                        <Route path="logs" element={
                            showLogs ? <AdminLogs /> : <Navigate to={defaultRoute} replace />
                        } />

                        <Route path="ui" element={<UI />} />

                        <Route path="profile-settings" element={<ProfileSettings />} />
                    </>
                )}

                {/* Fundraising Routes */}
                {section === 'fundraising' && (
                    <>
                        {/* Your Raises - List View */}
                        <Route path="raises" element={
                            showRaises ? <RaiseList /> : <Navigate to={defaultRoute} replace />
                        } />
                        
                        {/* Raise Detail - Full width with internal navigation */}
                        <Route path="raises/:raiseId" element={
                            showRaises ? <RaiseDetail /> : <Navigate to={defaultRoute} replace />
                        } />

                        {/* Analytics - Comprehensive view for all raises */}
                        <Route path="analytics" element={
                            showAnalytics ? <AllRaisesAnalytics /> : <Navigate to={defaultRoute} replace />
                        } />

                        {/* Fund Detail */}
                        <Route path="funds/:fundId/*" element={<FundDetail />} />

                        {/* Raise Settings */}
                        <Route path="raises/:fundraiseId/settings" element={<RaiseSettings />} />

                        {/* Term Sheet Settings */}
                        <Route path="term-sheet-settings" element={<TermSheetSettings />} />

                        {/* VDR */}
                        <Route path="vdr" element={
                            showVDR ? <VDR /> : <Navigate to={defaultRoute} replace />
                        } />
                        
                        {/* Domain Groups */}
                        <Route path="domain-groups" element={
                            showDomainGroups ? <DomainGroups /> : <Navigate to={defaultRoute} replace />
                        } />

                        {/* VDR Sub-routes */}
                        <Route path="vdr/:id/settings" element={<VDRSettings />} />
                    </>
                )}

                {/* Holdings Routes */}
                {section === 'holdings' && (
                    <>
                        {/* Overview */}
                        <Route path="overview" element={<HoldingsOverview />} />

                        {/* Cost Analytics */}
                        <Route path="costs/analytics" element={
                            showHoldingsCosts ? <CostsByView /> : <Navigate to="/holdings/overview" replace />
                        } />

                        {/* Portfolio - Companies & People */}
                        <Route path="portfolio" element={
                            showHoldingsPortfolio ? <CompanyList /> : <Navigate to="/holdings/overview" replace />
                        } />

                        <Route path="portfolio/:id" element={
                            showHoldingsPortfolio ? <CompanyDetail /> : <Navigate to="/holdings/overview" replace />
                        } />

                        <Route path="people" element={
                            showHoldingsPortfolio ? <PeopleList /> : <Navigate to="/holdings/overview" replace />
                        } />

                        {/* Costs */}
                        <Route path="costs" element={
                            showHoldingsCosts ? <CostsDashboard /> : <Navigate to="/holdings/overview" replace />
                        } />

                        <Route path="costs/subscriptions/:id" element={
                            showHoldingsCosts ? <SubscriptionDetail /> : <Navigate to="/holdings/overview" replace />
                        } />

                        {/* Transactions */}
                        <Route path="transactions" element={
                            showHoldingsTransactions ? <TransactionList /> : <Navigate to="/holdings/overview" replace />
                        } />

                        {/* Bank Imports */}
                        <Route path="bank-imports" element={
                            showHoldingsBankImports ? <BankImportDashboard /> : <Navigate to="/holdings/overview" replace />
                        } />

                        <Route path="bank-imports/upload" element={
                            showHoldingsBankImports ? <ImportUpload /> : <Navigate to="/holdings/overview" replace />
                        } />

                        <Route path="bank-imports/review" element={
                            showHoldingsBankImports ? <TransactionReview /> : <Navigate to="/holdings/overview" replace />
                        } />

                        <Route path="bank-imports/mappings" element={
                            showHoldingsBankImports ? <VendorMappings /> : <Navigate to="/holdings/overview" replace />
                        } />

                        <Route path="bank-imports/mercury" element={
                            showHoldingsBankImports ? <MercurySetup /> : <Navigate to="/holdings/overview" replace />
                        } />

                        {/* Planning */}
                        <Route path="planning" element={
                            showHoldingsPlanning ? <PlanList /> : <Navigate to="/holdings/overview" replace />
                        } />

                        <Route path="planning/:id" element={
                            showHoldingsPlanning ? <PlanDetail /> : <Navigate to="/holdings/overview" replace />
                        } />

                        <Route path="planning/:id/compare" element={
                            showHoldingsPlanning ? <PlanComparison /> : <Navigate to="/holdings/overview" replace />
                        } />

                        {/* Revenue */}
                        <Route path="revenue" element={
                            showHoldingsRevenue ? <RevenueList /> : <Navigate to="/holdings/overview" replace />
                        } />

                        <Route path="revenue/import" element={
                            showHoldingsRevenue ? <RevenueImport /> : <Navigate to="/holdings/overview" replace />
                        } />

                        <Route path="revenue/margins" element={
                            showHoldingsRevenue ? <CompanyMargins /> : <Navigate to="/holdings/overview" replace />
                        } />

                        <Route path="revenue/forecasts" element={
                            showHoldingsRevenue ? <RevenueForecastList /> : <Navigate to="/holdings/overview" replace />
                        } />

                        {/* Analytics */}
                        <Route path="analytics/expected-vs-actual" element={
                            showHoldingsRevenue ? <ExpectedVsActualDashboard /> : <Navigate to="/holdings/overview" replace />
                        } />
                    </>
                )}

                {/* No Access Page */}
                <Route path="no-access" element={
                    <div className="text-center py-20 text-neutral-500">
                        <h2 className="text-xl font-semibold mb-2">Access Denied</h2>
                        <p>You do not have permission to view any pages.</p>
                    </div>
                } />

                {/* Catch all */}
                <Route path="*" element={
                    <div className="text-center py-20 text-neutral-500">
                        <h2 className="text-xl font-semibold mb-2">Page Not Found</h2>
                        <p>The page you are looking for does not exist.</p>
                        <Button className="mt-4" onClick={() => navigate(defaultRoute)} text="Go Home" />
                    </div>
                } />
            </Routes>

            {!isRaiseDetail && (
                <footer className="mt-20 pt-6 border-t border-neutral-100">
                <div className="flex items-center justify-between text-[12px] text-neutral-500">
                    <div>By all means, accelerate.</div>
                    <div>o37</div>
                </div>
                </footer>
            )}
        </MainContent>
      </div>

      {/* AI Assistant Sidebar */}
      <AIAssistantSidebar 
        isOpen={aiSidebarOpen} 
        onClose={() => setAiSidebarOpen(false)}
        fundraiseId={currentFundraiseId}
      />
    </SidebarLayout>
  );
}

export default function AdminPanel(props: AdminPanelProps) {
  return (
    <PermissionProvider>
      <AdminPanelContent {...props} />
    </PermissionProvider>
  );
}
